import type { LegacyNoteBlock } from "@/components/shapes/doc-frame/doc-legacy-note";
import {
  asRecord,
  asString,
  compact,
  recordsToText,
  type PlainRecord,
} from "./plain-record";

/** Plain text of V2 blocks: lists, checklists and frame links as text. */
function legacyBlocksText(blocks: LegacyNoteBlock[]): string {
  let numberIndex = 0;
  return blocks
    .map((b) => {
      if (b.type === "frame_link") return `[@${b.content}]`;
      if (b.type === "todo") return `${b.checked ? "[x]" : "[ ]"} ${b.content}`;
      if (b.style === "bullet") return `- ${b.content}`;
      if (b.style === "number") {
        numberIndex++;
        return `${numberIndex}. ${b.content}`;
      }
      numberIndex = 0;
      return b.content;
    })
    .join("\n");
}

function childrenOf(node: PlainRecord): PlainRecord[] {
  return Array.isArray(node.content)
    ? node.content
        .map((child) => asRecord(child))
        .filter((child): child is PlainRecord => child !== null)
    : [];
}

function attrsOf(node: PlainRecord): PlainRecord {
  return asRecord(node.attrs) ?? {};
}

function inlineNodeText(node: PlainRecord): string {
  if (node.type === "text") return asString(node.text);
  if (node.type === "hardBreak") return "\n";

  const attrs = attrsOf(node);
  if (node.type === "mention" || node.type === "frameMention") {
    const label = asString(attrs.label) || asString(attrs.id);
    return label ? `@${label}` : "";
  }
  if (node.type === "image") {
    return compact([
      asString(attrs.alt),
      asString(attrs.title),
      asString(attrs.src),
    ]);
  }
  return childrenOf(node).map(inlineNodeText).join("");
}

function prefixMultiline(text: string, prefix: string, continuation: string) {
  const lines = text.split("\n");
  return lines
    .map((line, index) => `${index === 0 ? prefix : continuation}${line}`)
    .join("\n");
}

function tiptapNodeToLines(node: PlainRecord): string[] {
  const type = asString(node.type);
  const children = childrenOf(node);

  if (type === "paragraph" || type === "heading" || type === "codeBlock") {
    return [inlineNodeText(node).trim()].filter(Boolean);
  }

  if (type === "bulletList") {
    return children
      .map((child) => tiptapNodeToLines(child).join("\n").trim())
      .filter(Boolean)
      .map((text) => prefixMultiline(text, "- ", "  "));
  }

  if (type === "orderedList") {
    const start = Number(attrsOf(node).start) || 1;
    return children
      .map((child, index) => {
        const text = tiptapNodeToLines(child).join("\n").trim();
        if (!text) return "";
        const prefix = `${start + index}. `;
        return prefixMultiline(text, prefix, " ".repeat(prefix.length));
      })
      .filter(Boolean);
  }

  if (type === "taskList") {
    return children
      .map((child) => {
        const text = tiptapNodeToLines(child).join("\n").trim();
        if (!text) return "";
        const checked = attrsOf(child).checked === true;
        return prefixMultiline(text, checked ? "[x] " : "[ ] ", "    ");
      })
      .filter(Boolean);
  }

  if (type === "horizontalRule") return ["---"];

  if (children.length > 0) {
    return children.flatMap(tiptapNodeToLines);
  }

  const text = inlineNodeText(node).trim();
  return text ? [text] : [];
}

/** Plain text of legacy note content (V1 text/todos, V2 blocks, V3 TipTap). */
export function noteContentToText(content: PlainRecord): string {
  const v1Text = asString(content.text);
  if (v1Text) return v1Text;

  const todos = recordsToText(content.todos, (todo) => {
    const label = asString(todo.text);
    if (!label) return "";
    return `${todo.checked ? "[x]" : "[ ]"} ${label}`;
  });
  if (todos) return todos;

  if (Array.isArray(content.blocks)) {
    return legacyBlocksText(content.blocks as LegacyNoteBlock[]);
  }

  const tiptap = asRecord(content.tiptap);
  if (!tiptap) return "";
  return tiptapNodeToLines(tiptap).join("\n").trim();
}
